import { supabase } from "@/infra/supabase/client";
import type { SupabaseClient } from "@supabase/supabase-js";
import type {
  Turma,
  TurmaMember,
  TempleReleaseRow,
  CreateTurmaInput,
  UpdateTurmaInput,
  CsvImportResult,
  ReleaseMode,
  TempleAbility,
} from "@/types/turma.types";

// turmas / turma_temple_releases ainda nao estao nos tipos gerados do Supabase.
// Usamos um client sem tipagem de schema para essas operacoes.
const db = supabase as unknown as SupabaseClient;

// ---------------------------------------------------------------------------
// Mapeadores
// ---------------------------------------------------------------------------
function mapTurma(row: Record<string, unknown>): Turma {
  return {
    id: row.id as number,
    name: row.name as string,
    description: (row.description as string | null) ?? null,
    releaseMode: row.release_mode as ReleaseMode,
    autoRelease: !!row.auto_release,
    startDate: (row.start_date as string | null) ?? null,
    cadenceDays: (row.cadence_days as number | null) ?? null,
    isEntryTurma: !!row.is_entry_turma,
    active: !!row.active,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

/** Calcula se um templo esta aberto agora (espelha turma_temple_open no SQL). */
function computeOpen(
  turma: Pick<Turma, "releaseMode" | "autoRelease" | "startDate" | "active">,
  release: {
    releaseAt: string | null;
    released: boolean;
    locked: boolean;
  } | null,
  now: Date = new Date(),
): boolean {
  if (!turma.active) return true;
  if (turma.releaseMode === "entry") {
    return !turma.startDate || new Date(turma.startDate) <= now;
  }
  // batch
  if (!release) return false;
  if (release.locked) return false;
  return (
    release.released ||
    (turma.autoRelease &&
      release.releaseAt !== null &&
      new Date(release.releaseAt) <= now)
  );
}

// ---------------------------------------------------------------------------
// CRUD de turmas
// ---------------------------------------------------------------------------
export const turmasService = {
  async list(): Promise<Turma[]> {
    const { data, error } = await db
      .from("turmas")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) throw error;
    const turmas = (data ?? []).map(mapTurma);

    // Contagem de membros por turma
    const { data: members, error: mErr } = await db
      .from("profiles")
      .select("turma_id")
      .not("turma_id", "is", null);
    if (mErr) throw mErr;
    const counts = new Map<number, number>();
    for (const m of members ?? []) {
      const id = (m as { turma_id: number }).turma_id;
      counts.set(id, (counts.get(id) ?? 0) + 1);
    }
    return turmas.map((t) => ({ ...t, memberCount: counts.get(t.id) ?? 0 }));
  },

  /** Lista enxuta de turmas ativas para selects (id + nome). */
  async listForSelect(): Promise<{ id: number; name: string }[]> {
    const { data, error } = await db
      .from("turmas")
      .select("id, name")
      .eq("active", true)
      .order("name", { ascending: true });
    if (error) throw error;
    return (data ?? []).map((r) => ({
      id: (r as { id: number }).id,
      name: (r as { name: string }).name,
    }));
  },

  async get(id: number): Promise<Turma | null> {
    const { data, error } = await db
      .from("turmas")
      .select("*")
      .eq("id", id)
      .maybeSingle();
    if (error) throw error;
    return data ? mapTurma(data) : null;
  },

  async create(input: CreateTurmaInput): Promise<Turma> {
    // Garante turma de entrada unica: se marcar esta, desmarca as demais
    if (input.isEntryTurma) await clearEntryFlag();
    const { data, error } = await db
      .from("turmas")
      .insert({
        name: input.name,
        description: input.description ?? null,
        release_mode: input.releaseMode,
        auto_release: input.autoRelease,
        start_date: input.startDate ?? null,
        cadence_days: input.cadenceDays ?? null,
        is_entry_turma: input.isEntryTurma ?? false,
        active: input.active ?? true,
      })
      .select()
      .single();
    if (error) throw error;
    return mapTurma(data);
  },

  async update(id: number, patch: UpdateTurmaInput): Promise<Turma> {
    if (patch.isEntryTurma) await clearEntryFlag(id);
    const dbPatch: Record<string, unknown> = {};
    if (patch.name !== undefined) dbPatch.name = patch.name;
    if (patch.description !== undefined)
      dbPatch.description = patch.description;
    if (patch.releaseMode !== undefined)
      dbPatch.release_mode = patch.releaseMode;
    if (patch.autoRelease !== undefined)
      dbPatch.auto_release = patch.autoRelease;
    if (patch.startDate !== undefined) dbPatch.start_date = patch.startDate;
    if (patch.cadenceDays !== undefined)
      dbPatch.cadence_days = patch.cadenceDays;
    if (patch.isEntryTurma !== undefined)
      dbPatch.is_entry_turma = patch.isEntryTurma;
    if (patch.active !== undefined) dbPatch.active = patch.active;

    const { data, error } = await db
      .from("turmas")
      .update(dbPatch)
      .eq("id", id)
      .select()
      .single();
    if (error) throw error;
    return mapTurma(data);
  },

  async remove(id: number): Promise<void> {
    const { error } = await db.from("turmas").delete().eq("id", id);
    if (error) throw error;
  },

  // -------------------------------------------------------------------------
  // Matricula
  // -------------------------------------------------------------------------
  async listMembers(turmaId: number): Promise<TurmaMember[]> {
    const { data, error } = await db
      .from("profiles")
      .select("id, full_name, email")
      .eq("turma_id", turmaId)
      .order("full_name", { ascending: true });
    if (error) throw error;
    return (data ?? []).map((r) => ({
      id: (r as { id: string }).id,
      fullName: (r as { full_name: string | null }).full_name,
      email: (r as { email: string | null }).email,
    }));
  },

  /** Busca alunos (nao admin) por nome/email para adicionar a uma turma. */
  async searchStudents(term: string, limit = 20): Promise<TurmaMember[]> {
    let q = db
      .from("profiles")
      .select("id, full_name, email")
      .eq("is_admin", false)
      .limit(limit);
    if (term.trim()) {
      const t = `%${term.trim()}%`;
      q = q.or(`full_name.ilike.${t},email.ilike.${t}`);
    }
    const { data, error } = await q;
    if (error) throw error;
    return (data ?? []).map((r) => ({
      id: (r as { id: string }).id,
      fullName: (r as { full_name: string | null }).full_name,
      email: (r as { email: string | null }).email,
    }));
  },

  /** Matricula (ou move) alunos para a turma. */
  async setMembers(turmaId: number, userIds: string[]): Promise<void> {
    if (userIds.length === 0) return;
    const { error } = await db
      .from("profiles")
      .update({ turma_id: turmaId })
      .in("id", userIds);
    if (error) throw error;
  },

  /** Remove alunos da turma (turma_id = null). */
  async removeMembers(userIds: string[]): Promise<void> {
    if (userIds.length === 0) return;
    const { error } = await db
      .from("profiles")
      .update({ turma_id: null })
      .in("id", userIds);
    if (error) throw error;
  },

  /** Import por emails. Casa com profiles existentes; reporta nao encontrados. */
  async importByEmails(
    turmaId: number,
    emails: string[],
  ): Promise<CsvImportResult> {
    const clean = Array.from(
      new Set(emails.map((e) => e.trim().toLowerCase()).filter(Boolean)),
    );
    if (clean.length === 0) return { matched: [], unmatched: [] };

    const { data, error } = await db
      .from("profiles")
      .select("id, email")
      .in("email", clean);
    if (error) throw error;

    const found = data ?? [];
    const foundEmails = new Set(
      found.map((r) => ((r as { email: string }).email || "").toLowerCase()),
    );
    const ids = found.map((r) => (r as { id: string }).id);
    if (ids.length) await this.setMembers(turmaId, ids);

    return {
      matched: clean.filter((e) => foundEmails.has(e)),
      unmatched: clean.filter((e) => !foundEmails.has(e)),
    };
  },

  // -------------------------------------------------------------------------
  // Cronograma de liberacao (modo batch)
  // -------------------------------------------------------------------------
  async getSchedule(turmaId: number): Promise<TempleReleaseRow[]> {
    const turma = await this.get(turmaId);
    const { data: temples, error: tErr } = await db
      .from("temples")
      .select("id, name, order_number, theme_color")
      .eq("active", true)
      .order("order_number", { ascending: true });
    if (tErr) throw tErr;

    const { data: releases, error: rErr } = await db
      .from("turma_temple_releases")
      .select("*")
      .eq("turma_id", turmaId);
    if (rErr) throw rErr;

    const relByTemple = new Map<number, Record<string, unknown>>();
    for (const r of releases ?? [])
      relByTemple.set((r as { temple_id: number }).temple_id, r);

    return (temples ?? []).map((t) => {
      const tt = t as {
        id: number;
        name: string;
        order_number: number;
        theme_color: string | null;
      };
      const rel = relByTemple.get(tt.id);
      const releaseAt = (rel?.release_at as string | null) ?? null;
      const released = !!rel?.released;
      const locked = !!rel?.locked;
      return {
        templeId: tt.id,
        templeName: tt.name,
        orderNumber: tt.order_number,
        themeColor: tt.theme_color,
        releaseAt,
        released,
        locked,
        isOpenNow: turma
          ? computeOpen(turma, rel ? { releaseAt, released, locked } : null)
          : false,
      };
    });
  },

  /**
   * O que cada templo abre: as habilidades ligadas a ele (temple_abilities),
   * na ordem de exibição. Serve pro cronograma mostrar o que a turma ganha
   * quando o templo abre. A ligação é global: vale pra toda turma.
   */
  async getTempleAbilities(): Promise<Record<number, TempleAbility[]>> {
    const { data: links, error: lErr } = await db
      .from("temple_abilities")
      .select("temple_id, ability_code, display_order")
      .order("display_order", { ascending: true });
    if (lErr) throw lErr;
    const { data: abilities, error: aErr } = await db
      .from("mentor_abilities")
      .select("code, name, ability_type, active");
    if (aErr) throw aErr;
    const porCodigo = new Map(
      (abilities ?? []).map((a) => [
        (a as { code: string }).code,
        a as {
          code: string;
          name: string;
          ability_type: string;
          active: boolean;
        },
      ]),
    );
    const resultado: Record<number, TempleAbility[]> = {};
    for (const l of links ?? []) {
      const link = l as { temple_id: number; ability_code: string };
      const a = porCodigo.get(link.ability_code);
      if (!a) continue;
      (resultado[link.temple_id] ??= []).push({
        code: a.code,
        name: a.name,
        abilityType: a.ability_type,
        active: !!a.active,
      });
    }
    return resultado;
  },

  /** Upsert de uma linha de cronograma. */
  async upsertRelease(
    turmaId: number,
    templeId: number,
    patch: { releaseAt?: string | null; released?: boolean; locked?: boolean },
  ): Promise<void> {
    const row: Record<string, unknown> = {
      turma_id: turmaId,
      temple_id: templeId,
    };
    if (patch.releaseAt !== undefined) row.release_at = patch.releaseAt;
    if (patch.released !== undefined) row.released = patch.released;
    if (patch.locked !== undefined) row.locked = patch.locked;
    const { error } = await db
      .from("turma_temple_releases")
      .upsert(row, { onConflict: "turma_id,temple_id" });
    if (error) throw error;
  },

  /** Libera o templo agora (manual): released = true, locked = false. */
  releaseNow(turmaId: number, templeId: number) {
    return this.upsertRelease(turmaId, templeId, {
      released: true,
      locked: false,
    });
  },

  /** Re-trava/esconde o templo pra turma (preserva progresso). */
  lockTemple(turmaId: number, templeId: number) {
    return this.upsertRelease(turmaId, templeId, { locked: true });
  },

  /** Destrava (remove o lock manual). */
  unlockTemple(turmaId: number, templeId: number) {
    return this.upsertRelease(turmaId, templeId, { locked: false });
  },

  /**
   * Gera datas por cadencia: templo i (na ordem) abre em
   * startDate + (i-1) * cadenceDays. Sobrescreve release_at, zera overrides.
   */
  async generateCadence(
    turmaId: number,
    startDate: string,
    cadenceDays: number,
  ): Promise<void> {
    const { data: temples, error } = await db
      .from("temples")
      .select("id, order_number")
      .eq("active", true)
      .order("order_number", { ascending: true });
    if (error) throw error;

    const base = new Date(startDate);
    const rows = (temples ?? []).map((t, idx) => {
      const d = new Date(base);
      d.setDate(d.getDate() + idx * cadenceDays);
      return {
        turma_id: turmaId,
        temple_id: (t as { id: number }).id,
        release_at: d.toISOString(),
        released: false,
        locked: false,
      };
    });
    if (rows.length === 0) return;
    const { error: upErr } = await db
      .from("turma_temple_releases")
      .upsert(rows, { onConflict: "turma_id,temple_id" });
    if (upErr) throw upErr;
  },
};

// ---------------------------------------------------------------------------
// Gate de acesso do aluno (usado no student-side para esconder templos)
// ---------------------------------------------------------------------------
export interface UserTempleGate {
  /** A turma do aluno impoe gating? (false = comportamento legado por progresso) */
  applies: boolean;
  mode: ReleaseMode | null;
  /** Modo entry: a jornada ja comecou? */
  entryOpen: boolean;
  /** Modo batch: templo aberto por id. */
  openByTemple: Map<number, boolean>;
}

/**
 * Calcula o gating de templos para um usuario com base na turma dele.
 * Sem turma (ou turma inativa) => applies=false (nada e bloqueado).
 */
export async function getUserTempleGate(
  userId: string,
): Promise<UserTempleGate> {
  const none: UserTempleGate = {
    applies: false,
    mode: null,
    entryOpen: true,
    openByTemple: new Map(),
  };

  const { data: profile, error: pErr } = await db
    .from("profiles")
    .select("turma_id")
    .eq("id", userId)
    .maybeSingle();
  if (pErr) throw pErr;
  const turmaId = (profile as { turma_id: number | null } | null)?.turma_id;
  if (!turmaId) return none;

  const { data: turmaRow, error: tErr } = await db
    .from("turmas")
    .select("*")
    .eq("id", turmaId)
    .maybeSingle();
  if (tErr) throw tErr;
  if (!turmaRow) return none;
  const turma = mapTurma(turmaRow);
  if (!turma.active) return none;

  if (turma.releaseMode === "entry") {
    return {
      applies: true,
      mode: "entry",
      entryOpen: !turma.startDate || new Date(turma.startDate) <= new Date(),
      openByTemple: new Map(),
    };
  }

  // batch
  const { data: releases, error: rErr } = await db
    .from("turma_temple_releases")
    .select("*")
    .eq("turma_id", turmaId);
  if (rErr) throw rErr;

  const openByTemple = new Map<number, boolean>();
  for (const r of releases ?? []) {
    const rel = r as {
      temple_id: number;
      release_at: string | null;
      released: boolean;
      locked: boolean;
    };
    openByTemple.set(
      rel.temple_id,
      computeOpen(turma, {
        releaseAt: rel.release_at,
        released: rel.released,
        locked: rel.locked,
      }),
    );
  }
  return { applies: true, mode: "batch", entryOpen: true, openByTemple };
}

/** Desmarca is_entry_turma de todas as outras turmas (mantem so a indicada). */
async function clearEntryFlag(exceptId?: number): Promise<void> {
  let q = db
    .from("turmas")
    .update({ is_entry_turma: false })
    .eq("is_entry_turma", true);
  if (exceptId !== undefined) q = q.neq("id", exceptId);
  const { error } = await q;
  if (error) throw error;
}
